/**
 * Configuration validation utilities
 * Ensures required environment variables are present at application startup
 * 
 * @module config/validate
 */

import { AUTH0_CONFIG } from "./auth";
import { createLogger } from "../shared/logger";

const log = createLogger("config:validate");

/**
 * List of required environment variables
 * Application will not start without these in production
 * 
 * Note: Currently defined inline in validation logic
 */

/**
 * List of optional but recommended environment variables
 * 
 * Note: Currently defined inline in validation logic
 */

/**
 * Validates that all required configuration is present
 * 
 * @throws {Error} If required configuration is missing in production
 * @returns {object} Validation result with warnings
 */
export function validateConfig(): {
  valid: boolean;
  missing: string[];
  warnings: string[];
} {
  const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV;
  const missing: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  if (!AUTH0_CONFIG.CLIENT_ID) {
    missing.push("VITE_PORTAL_AUTH0_CLIENT_ID or VITE_EXTENSION_AUTH0_CLIENT_ID");
  }

  // In production, missing required variables are fatal
  if (!isDev && missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(", ")}\n` +
      `These must be set before building for production.`
    );
  }

  // In development, just warn
  if (isDev && missing.length > 0) {
    log.warn(
      `Missing environment variables: ${missing.join(", ")}\n` +
      `Application may not function correctly.`
    );
  }

  if (warnings.length > 0) {
    warnings.forEach((warning) => log.warn(warning));
  }

  return {
    valid: missing.length === 0,
    missing,
    warnings,
  };
}

/**
 * Get configuration summary for debugging
 * Safe to log - does not expose secrets
 */
export function getConfigSummary(): {
  auth0Domain: string;
  auth0ClientIdConfigured: boolean;
  apiBase: string;
  environment: string;
} {
  const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV;

  return {
    auth0Domain: String(AUTH0_CONFIG.DOMAIN),
    auth0ClientIdConfigured: typeof AUTH0_CONFIG.CLIENT_ID === "string" && AUTH0_CONFIG.CLIENT_ID.length > 0,
    apiBase: String(AUTH0_CONFIG.AUDIENCE),
    environment: isDev ? "development" : "production",
  };
}
